/**
 * Everflow tracking service
 * Handles conversion and event tracking for Everflow affiliate program
 */

export interface EverflowConversionParams {
  offer_id?: string;
  aid?: string; // advertiser_id
  transaction_id?: string;
  amount?: number; // Sale amount for RPS offers
  event_id?: string; // Event ID for tracking
  coupon_code?: string;
  adv_event_id?: string; // Global advertiser event
  order_id?: string;
  user_id?: string;
  verification_token?: string;
  email?: string;
  // Advertiser placement values
  adv1?: string;
  adv2?: string;
  adv3?: string;
  adv4?: string;
  adv5?: string;
}

/**
 * Check if Everflow SDK is loaded
 */
export const isEverflowAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  return typeof (window as any).EF !== 'undefined';
};

/**
 * Wait for Everflow SDK to be available
 */
export const waitForEverflow = async (maxAttempts = 50): Promise<boolean> => {
  return new Promise((resolve) => {
    let attempts = 0;

    const checkEF = () => {
      if (isEverflowAvailable()) {
        resolve(true);
        return;
      }

      attempts++;
      if (attempts >= maxAttempts) {
        console.error('Everflow SDK not available after maximum attempts');
        resolve(false);
        return;
      }

      setTimeout(checkEF, 100); // Check every 100ms
    };

    checkEF();
  });
};

/**
 * Track an Everflow conversion event
 * @param params Conversion parameters
 * @returns Promise with transaction_id and conversion_id
 */
export const trackEverflowConversion = async (
  params: EverflowConversionParams
): Promise<{ transaction_id: string; conversion_id: string } | null> => {
  if (!isEverflowAvailable()) {
    const loaded = await waitForEverflow();
    if (!loaded) {
      if (typeof (window as any).TrackJS !== 'undefined') {
        (window as any).TrackJS.track('Everflow SDK not available for conversion tracking');
      }
      return null;
    }
  }

  try {
    const EF = (window as any).EF;

    // Get stored transaction ID if not provided
    if (!params.transaction_id) {
      params.transaction_id = EF.getAdvertiserTransactionId('1'); // advertiser_id = 1
    }

    const response = await EF.conversion(params);

    console.log('Everflow conversion tracked:', response);
    return response;
  } catch (error) {
    console.error('Everflow conversion tracking failed:', error);
    if (typeof (window as any).TrackJS !== 'undefined') {
      (window as any).TrackJS.track(`Everflow conversion failed: ${error}`);
    }
    return null;
  }
};

/**
 * Track Everflow conversion with retry logic
 */
export const trackEverflowConversionWithRetry = async (
  params: EverflowConversionParams,
  maxRetries = 3
): Promise<{ transaction_id: string; conversion_id: string } | null> => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const result = await trackEverflowConversion(params);

    if (result) {
      return result;
    }

    if (attempt < maxRetries) {
      await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
    }
  }

  return null;
};

/**
 * Get the current Everflow transaction ID
 */
export const getEverflowTransactionId = (): string | null => {
  if (!isEverflowAvailable()) {
    return null;
  }

  const EF = (window as any).EF;
  return EF.getAdvertiserTransactionId('1');
};
